import React from 'react';
import { cn } from '../lib/utils';
import Button from './Button';
import { Badge } from './Badge';
import { Assessment } from '../src/types';
import { Plus, Calendar, FileText, Users, ArrowLeft, BookmarkPlus } from 'lucide-react';

interface AssessmentHeaderProps {
  assessment: Assessment;
  essayCount: number;
  selectedCount?: number;
  onMarkNewEssay: () => void;
  onBatchRemark?: () => void;
  onBack?: () => void;
  onSaveAsTemplate?: () => void;
  canSaveAsTemplate?: boolean;
  isLoading?: boolean;
}

export function AssessmentHeader({
  assessment,
  essayCount,
  selectedCount = 0,
  onMarkNewEssay,
  onBatchRemark,
  onBack,
  onSaveAsTemplate,
  canSaveAsTemplate = true,
  isLoading = false
}: AssessmentHeaderProps) {
  const yearLevelDisplay = assessment.yearLevel?.toString().toLowerCase().includes('year')
    ? assessment.yearLevel
    : `Year ${assessment.yearLevel}`;

  const createdDate = assessment.createdAt 
    ? new Date(assessment.createdAt).toLocaleDateString('en-AU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    : '';

  return (
    <div className="space-y-4">
      {/* Back Button - Prominent and Above Content */}
      {onBack && (
        <Button
          onClick={onBack}
          variant="outline"
          size="lg"
          className="group gap-2 px-5 py-3 text-base font-medium shadow-sm hover:shadow-md hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all duration-200"
        >
          <ArrowLeft className="h-5 w-5 transition-transform group-hover:-translate-x-1" />
          Back to All Assessments
        </Button>
      )}

      {/* Main Header Card */}
      <div className="bg-card rounded-xl border border-border overflow-hidden">
        {/* Accent bar */}
        <div className="h-1 bg-gradient-to-r from-primary to-primary/60" />
        
        <div className="p-6">
          {/* Title & Actions */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="space-y-1">
              <h1 className="text-2xl font-bold text-foreground">
                {assessment.assessmentNumber && (
                  <span className="text-muted-foreground font-normal">#{assessment.assessmentNumber} </span>
                )}
                {assessment.name}
              </h1>
              <p className="text-muted-foreground flex items-center gap-2">
                <Users className="h-4 w-4" />
                {essayCount} essay{essayCount !== 1 ? 's' : ''} submitted
              </p>
            </div>
            
            <div className="flex flex-wrap items-center gap-2">
              <Button
                onClick={onMarkNewEssay}
                disabled={isLoading}
                className="gap-2"
              >
                <Plus className="h-4 w-4" />
                Mark New Essay
              </Button>
              {onSaveAsTemplate && (
                <Button
                  onClick={onSaveAsTemplate}
                  disabled={isLoading || !canSaveAsTemplate}
                  variant="secondary"
                  className="gap-2"
                  title={canSaveAsTemplate
                    ? 'Save this rubric as a reusable template'
                    : 'This assessment has no structured marking criteria to save as a template'}
                >
                  <BookmarkPlus className="h-4 w-4" />
                  Save Rubric as Template
                </Button>
              )}
              {selectedCount > 0 && onBatchRemark && (
                <Button
                  onClick={onBatchRemark}
                  disabled={isLoading}
                  variant="secondary"
                  className="gap-2"
                >
                  Re-mark Selected ({selectedCount})
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Assessment Details Card */}
      <div className="bg-card rounded-xl border border-border p-5">
        {/* Tags */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <Badge variant="secondary">{yearLevelDisplay}</Badge>
          {assessment.subject && (
            <Badge variant="outline">{assessment.subject}</Badge>
          )}
          {assessment.year && (
            <Badge variant="outline">{assessment.year}</Badge>
          )}
          {assessment.examCondition && (
            <Badge variant="outline" className="text-green-700 border-green-200 bg-green-50">
              {assessment.examCondition}
            </Badge>
          )}
          {assessment.assessmentType && (
            <Badge variant="outline" className="text-amber-700 border-amber-200 bg-amber-50">
              {assessment.assessmentType}
            </Badge>
          )}
          
          {/* Created date */}
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground ml-auto">
            <Calendar className="h-3.5 w-3.5" />
            <span>Created {createdDate || 'Unknown'}</span>
          </div>
        </div>

        {/* Question */}
        {assessment.question && (
          <div className="bg-muted/50 rounded-lg p-4 border-l-2 border-primary/40">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
              Assessment Question
            </p>
            <p className="text-sm text-foreground leading-relaxed">
              {assessment.question}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default AssessmentHeader;
